import React, { Fragment } from 'react';
import { Breakpoint } from 'react-socks';
import { common } from '@kurtosys/ksys-app-template';
import { IFormatter } from '@kurtosys/types/data/IFormatter.js';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from 'recharts';

import { IPieProps } from '../../../models/charting/index.js';
import { InjectedStyledComponent, ResponsiveComponentBase } from '../../../shared/index.js';
import { ITableProps } from '../../base/Table/models/ITableProps.js';
import { TranslationContext } from '../../base/Translate/Translate.js';
import Disclaimers from '../../overview/Disclaimers/Disclaimers.js';
import { applyFormatters, applyTranslations } from '../ChartBase/ChartBaseFunctions.js';

import { IPieDoughnutChartConfiguration, IPieDoughnutChartProps, IPieDoughnutChartState } from './models/index.js';
import { IPieDoughnutElement } from './models/IPieDoughnutChartElement.js';
import DisclaimersWrapper from './styledComponents/DisclaimersWrapper.js';
import Wrapper from './styledComponents/Wrapper.js';
import { PIE_DOUGHNUT_CHART_CATEGORY_KEY, PIE_DOUGHNUT_CHART_COMPONENT_KEY } from './PieDoughnutChart.meta.js';

export class PieDoughnutChart extends ResponsiveComponentBase<
	IPieDoughnutChartProps,
	IPieDoughnutChartState,
	IPieDoughnutChartConfiguration
> {
	categoryKey = PIE_DOUGHNUT_CHART_CATEGORY_KEY;
	componentKey = PIE_DOUGHNUT_CHART_COMPONENT_KEY;
	// eslint-disable-next-line @typescript-eslint/naming-convention
	RADIAN = Math.PI / 180;
	tableFormatter: Partial<IFormatter> = {};

	pieDoughnutState: IPieDoughnutChartState = {
		activeIndex: null,
	};

	constructor(props: IPieDoughnutChartProps) {
		super(props);

		const { activeIndex } = props;
		if (!common.commonUtils.isNullOrUndefined(activeIndex)) {
			this.pieDoughnutState.activeIndex = activeIndex;
			this.pieDoughnutState = Object.assign({}, this.pieDoughnutState);
		}

		makeObservable(this, {
			pieDoughnutState: observable,
			componentDidUpdate: action,
			onPieEnter: action,
			onPieLeave: action,
		});
	}

	componentDidUpdate(previousProps: IPieDoughnutChartProps) {
		if (previousProps.activeIndex !== this.props.activeIndex) {
			this.pieDoughnutState.activeIndex = this.props.activeIndex;
			this.pieDoughnutState = Object.assign({}, this.pieDoughnutState);
		}
	}

	getFormatter(element: IPieDoughnutElement, type: 'value' | 'label'): (value: any) => any {
		if (type === 'value') {
			if (element.props && element.props.formatter) {
				return element.props.formatter as (value: any) => any;
			}
		}
		if (type === 'label') {
			if (element.props && element.props.labelFormatter) {
				return element.props.labelFormatter as (value: any) => any;
			}
		}
		return (value) => value;
	}

	applyLabelFormatting = (element: IPieDoughnutElement) => {
		const { props } = element;
		if (element.type === 'Pie' && props) {
			if (typeof props.label === 'object') {
				const { showLabel = false, showValue = false } = props.label;
				const valueFormatter = this.getFormatter(element, 'value');
				const labelFormatter = this.getFormatter(element, 'label');
				props.label = (labelProps: any) => {
					return `${showLabel ? labelFormatter(labelProps.name) : ''} ${
						showValue ? valueFormatter(labelProps.value) : ''
					}`;
				};
			} else if (typeof props.label === 'boolean' && props.label) {
				const valueFormatter = this.getFormatter(element, 'value');
				props.label = (labelProps: any) => {
					return valueFormatter(labelProps.value);
				};
			}
		}
	};

	renderActiveShape = (shapeProps: any) => {
		const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, value, name } = shapeProps;
		const {
			textPosition = 'outside',
			showActiveSection = true,
			elements = [],
			textYOffset,
			activeSectionWidth = 4,
			activeSectionStart = 6,
			tooltip,
		} = this.props;
		const { labelOptions = {} } = tooltip || {};
		let { toolTipColor = '#000' } = labelOptions;
		const { hideNameLabel } = labelOptions;
		const sin = Math.sin(-this.RADIAN * midAngle);
		const cos = Math.cos(-this.RADIAN * midAngle);
		const sx = cx + (outerRadius + 10) * cos;
		const sy = cy + (outerRadius + 10) * sin;
		const mx = cx + (outerRadius + 30) * cos;
		const my = cy + (outerRadius + 30) * sin;
		const ex = mx + (cos >= 0 ? 1 : -1) * 22;

		const textAnchor = cos >= 0 ? 'start' : 'end';
		const valueFormatter = this.getFormatter(elements[0], 'value');
		const labelFormatter = this.getFormatter(elements[0], 'label');
		if (toolTipColor === 'inherit') {
			toolTipColor = fill;
		}
		return (
			<g>
				{
					// For when the tooltip text should display inside the chart
					textPosition === 'inside' && (
						<Fragment>
							{!hideNameLabel && (
								<text
									x={cx}
									y={cy - 5}
									dy={typeof textYOffset === 'number' ? textYOffset : 8}
									textAnchor="middle"
									className="tooltipLabel"
								>
									{labelFormatter(name)}
								</text>
							)}
							<text
								x={cx}
								y={!hideNameLabel ? cy + 5 : cy}
								dy={typeof textYOffset === 'number' ? textYOffset : 8}
								textAnchor="middle"
								className="tooltipValue"
							>
								{valueFormatter(value)}
							</text>
						</Fragment>
					)
				}
				{/* The pie section on the chart */}
				<Sector
					{...{
						cx,
						cy,
						innerRadius,
						outerRadius,
						startAngle,
						endAngle,
						fill,
					}}
				/>
				{
					// This is the outside line that follows the section outside edge, to indicate the active
					showActiveSection && (
						<Sector
							{...{ cx, cy, startAngle, endAngle }}
							innerRadius={outerRadius + activeSectionStart}
							outerRadius={outerRadius + activeSectionStart + activeSectionWidth}
							fill={toolTipColor}
						/>
					)
				}
				{
					// This is the outside tooltip where it will draw a line from the pie section to the tooltip text that terminates with a circle at the end
					textPosition === 'outside' && (
						<Fragment>
							<path d={`M${sx},${sy}L${mx},${my}L${ex},${my}`} stroke={toolTipColor} fill="none" />
							<circle cx={ex} cy={my} r={2} fill={toolTipColor} stroke="none" />
							<text
								x={ex + (cos >= 0 ? 1 : -1) * 12}
								y={my}
								dy={typeof textYOffset === 'number' ? textYOffset : 0}
								textAnchor={textAnchor}
								fill={toolTipColor}
							>
								{!hideNameLabel && <tspan className="tooltipLabel">{`${labelFormatter(name)}`}</tspan>}{' '}
								<tspan className="tooltipValue">{`${valueFormatter(value)}`}</tspan>
							</text>
						</Fragment>
					)
				}
				{
					// For when the tooltip text should display at the bottom of the chart
					textPosition === 'bottom' && (
						<g>
							<text
								x={cx}
								y={2 * cy}
								dy={typeof textYOffset === 'number' ? textYOffset : -5}
								textAnchor="middle"
							>
								{!hideNameLabel && <tspan className="tooltipLabel">{labelFormatter(name)}</tspan>}{' '}
								<tspan className="tooltipValue">{valueFormatter(value)}</tspan>
							</text>
						</g>
					)
				}
			</g>
		);
	};

	onPieEnter = (data: any, index: number) => {
		if (this.pieDoughnutState.activeIndex !== index) {
			this.pieDoughnutState.activeIndex = index;
			this.pieDoughnutState = Object.assign({}, this.pieDoughnutState);
		}
	};

	onPieLeave = () => {
		this.pieDoughnutState.activeIndex = null;
		this.pieDoughnutState = Object.assign({}, this.pieDoughnutState);
	};

	renderPieChart = (props?: IPieDoughnutChartProps) => {
		if (!props) {
			return null;
		}
		const {
			// Fix build issue. bad typings
			responsiveContainerProps = {} as any,
			elements = [],
			disclaimersProps,
			startAngle,
			endAngle,
			cx,
			cy,
			showTooltip = true,
			singleValueDisplay,
			tooltip,
		} = props;

		const { type: tooltipType, popoverOptions = {}  } = tooltip || {}
		return (
			<Fragment>
				<TranslationContext.Consumer>
					{(translationContext) => {
						return (
							<div aria-hidden={true}>
								<ResponsiveContainer {...responsiveContainerProps} minHeight="300px">
									<PieChart {...props}>
										{elements.map((element, elementIndex, elementArray) => {
											applyTranslations(element, translationContext);
											applyFormatters(element, translationContext);
											this.applyLabelFormatting(element);
											const { props, colors = [] } = element;
											const mergeProps = { startAngle, endAngle, cx, cy };
											if (showTooltip && elementIndex === elementArray.length - 1 && tooltipType === "label") {
												Object.assign(mergeProps, {
													activeIndex: this.pieDoughnutState.activeIndex,
													activeShape: this.renderActiveShape,
													onMouseEnter: this.onPieEnter,
													onClick: this.onPieEnter,
													onMouseLeave: this.onPieLeave,
												});
											}
											if (singleValueDisplay) {
												Object.assign(mergeProps, {
													activeIndex: 0,
													activeShape: this.renderActiveShape,
												});
											}
											Object.assign(props || {}, mergeProps || {});
											return (
												<Fragment key={elementIndex}>
													<Pie {...(props as IPieProps)}>
														{props &&
															props.data &&
															props.data.map((elem, idx) => {
																return (
																	<Cell
																		key={`cell-${idx}`}
																		fill={colors[idx % colors.length]}
																	/>
																);
															})}
													</Pie>
													{ (showTooltip && tooltipType === "popover") && <Tooltip {...popoverOptions} /> }
												</Fragment>
											);
										})}
									</PieChart>
								</ResponsiveContainer>
							</div>
						);
					}}
				</TranslationContext.Consumer>
				{disclaimersProps && (
					<DisclaimersWrapper>
						<Disclaimers {...disclaimersProps} />
					</DisclaimersWrapper>
				)}
			</Fragment>
		);
	};

	getTableProps(props?: IPieProps): ITableProps {
		if (props) {
			const dataKey = props.dataKey.toString();
			if (common.commonUtils.isNullOrEmpty(this.tableFormatter)) {
				// Due to the formatter being updated in ChartBaseFunctions.tsx line 387, we need to store the format config for the table
				this.tableFormatter = props.formatter && typeof props.formatter !== 'function' ? props.formatter : {};
			}
			return {
				columns: [
					{
						id: 'name',
						key: 'name',
						header: '',
					},
					{
						id: dataKey,
						key: dataKey,
						header: 'Value',
						format: this.tableFormatter,
					},
				],
				rows: props.data,
			};
		}
		return {};
	}

	render() {
		const { className } = this.props;
		const breakpoints = this.breakpoints;
		this.pieDoughnutState;
		return (
			<Fragment>
				{breakpoints.map((breakpoint, breakpointIndex) => {
					const { props } = breakpoint;
					const breakpointProps = this.getBreakpointComponentProps(breakpoint, breakpointIndex);
					return (
						<Breakpoint key={breakpointIndex} {...breakpointProps}>
							<Wrapper className={className}>{this.renderPieChart(props)}</Wrapper>
						</Breakpoint>
					);
				})}
			</Fragment>
		);
	}
}

export default InjectedStyledComponent(
	observer(PieDoughnutChart),
	PIE_DOUGHNUT_CHART_CATEGORY_KEY,
	PIE_DOUGHNUT_CHART_COMPONENT_KEY,
);
